import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { type EBorrowPosition, useEBorrowPositions } from '@/domain/euler/positions/useEBorrowPositions'
import { useELendPositions } from '@/domain/euler/positions/useELendPositions'
import BigNumber from 'bignumber.js'
import { useMemo } from 'react'
import { useAccount } from 'wagmi'
import { STABLECOIN_LABELS } from '../constants'
import { useSolverOptions } from './useSolverOptions'

export interface DebtVaultInfo {
  liabilityVaultAddress: string
  stableSymbol: string
  borrowAmount: number
  /** Whether this debt vault is in the backend's supported vaults list */
  isSupported: boolean
}

export interface CollateralInfo {
  vaultAddress: string
  tokenSymbol: string
  depositAmount: number
  depositAmountUSD: number
}

export interface MergedPosition {
  subAccountId: number
  /** Borrow positions with debt */
  debtVaults: DebtVaultInfo[]
  /** Collateral deposits on this sub-account */
  collateral: CollateralInfo[]
  /** All raw eBorrow positions for this sub-account */
  borrowPositions: EBorrowPosition[]
  /** Whether this sub-account has any position (debt or collateral) */
  hasAnyPosition: boolean
  /** Whether this sub-account only has collateral (no debt yet) */
  collateralOnly: boolean
  /** Whether any debt vault in this position is NOT in the backend's supported list */
  hasUnsupportedDebt: boolean
  /** Human-readable label like "Position 3 · USDC, USDT0" or "Position 0 · Collateral only" */
  label: string
}

/**
 * Uses on-chain sub-account discovery + useEBorrowPositions + useELendPositions to find
 * all Euler positions (debt or collateral), grouped by subAccountId.
 */
export function useAllEulerPositions(options?: { enabled?: boolean }) {
  const enabled = options?.enabled !== false
  const { address } = useAccount()
  const { eulerBorrowVaults } = useSolverOptions()

  const supportedVaultAddresses = useMemo(
    () => new Set(eulerBorrowVaults.map((vault) => vault.address.toLowerCase())),
    [eulerBorrowVaults],
  )

  const { positions: allBorrowPositions, isLoading: isBorrowLoading } = useEBorrowPositions({
    userAddress: address,
    chainId: E_LEND_CHAIN_ID,
    enabled: enabled && !!address,
  })

  const { positions: allLendPositions, isLoading: isLendLoading } = useELendPositions({
    userAddress: address,
    chainId: E_LEND_CHAIN_ID,
    enabled: enabled && !!address,
  })

  const positions = useMemo(() => {
    const borrowBySubAccount = new Map<number, EBorrowPosition[]>()
    const collateralBySubAccount = new Map<number, CollateralInfo[]>()

    // Group borrow positions by sub-account (no vault filter — show all debt like the dashboard)
    for (const pos of allBorrowPositions) {
      const existing = borrowBySubAccount.get(pos.subAccountId) ?? []
      existing.push(pos)
      borrowBySubAccount.set(pos.subAccountId, existing)
    }

    // Group collateral (lend) positions by sub-account
    for (const pos of allLendPositions) {
      if (new BigNumber(pos.depositAmount).lte(0)) continue

      const existing = collateralBySubAccount.get(pos.subAccountId) ?? []
      existing.push({
        vaultAddress: pos.vaultAddress,
        tokenSymbol: pos.token.symbol,
        depositAmount: new BigNumber(pos.depositAmount).toNumber(),
        depositAmountUSD: new BigNumber(pos.depositAmountUSD).toNumber(),
      })
      collateralBySubAccount.set(pos.subAccountId, existing)
    }

    // Merge: union of all sub-accounts that have either debt or collateral
    const allSubAccountIds = new Set([...borrowBySubAccount.keys(), ...collateralBySubAccount.keys()])

    const merged: MergedPosition[] = []
    for (const subAccountId of allSubAccountIds) {
      const borrowPositions = borrowBySubAccount.get(subAccountId) ?? []
      const collateral = collateralBySubAccount.get(subAccountId) ?? []

      const debtVaults: DebtVaultInfo[] = borrowPositions.map((pos) => {
        const lowerAddr = pos.liabilityToken.address.toLowerCase()
        return {
          liabilityVaultAddress: pos.liabilityVaultAddress,
          stableSymbol: STABLECOIN_LABELS[lowerAddr] ?? pos.liabilityToken.symbol,
          borrowAmount: pos.borrowAmount.toNumber(),
          isSupported: supportedVaultAddresses.has(pos.liabilityVaultAddress.toLowerCase()),
        }
      })

      const collateralOnly = debtVaults.length === 0 && collateral.length > 0
      const hasUnsupportedDebt = debtVaults.some((d) => !d.isSupported)
      const uniqueStables = [...new Set(debtVaults.map((d) => d.stableSymbol))]
      const uniqueCollateral = [...new Set(collateral.map((c) => c.tokenSymbol))]

      let label: string
      if (collateralOnly) {
        label = `Position ${subAccountId} · Deposits: ${uniqueCollateral.join(', ')}`
      } else {
        const parts = [...uniqueStables]
        label = `Position ${subAccountId} · ${parts.join(', ')}`
      }

      merged.push({
        subAccountId,
        debtVaults,
        collateral,
        borrowPositions,
        hasAnyPosition: true,
        collateralOnly,
        hasUnsupportedDebt,
        label,
      })
    }

    merged.sort((a, b) => a.subAccountId - b.subAccountId)
    return merged
  }, [allBorrowPositions, allLendPositions, supportedVaultAddresses])

  return {
    positions,
    isLoading: isBorrowLoading || isLendLoading,
    hasError: false,
  }
}
